import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';

/**
 * A custom element that manages cart drawer behavior within a `<theme-drawer>`.
 *
 * Dialog lifecycle (open/close, squeeze, history, animations) is owned by `<theme-drawer>`.
 * The `cart:view` event is auto-dispatched by `CartItemsComponent` via the
 * `view-event-trigger="dialog"` attribute (see `snippets/cart-items-component.liquid`).
 * Cart count announcements are owned by `<header-actions>`.
 * This component handles the remaining cart-specific concerns: auto-open on add-to-cart,
 * sticky summary layout, installments CTA close-on-click, and Lissa cart attributes.
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {number | undefined} */
  #cedulaSaveTimer;

  /** @type {string} */
  #lastCedulaSaved = '';

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (
      this.closest('theme-drawer')
    );
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(
      StandardEvents.cartLinesUpdate,
      this.#handleCartLinesUpdate
    );

    this.#themeDrawer?.addEventListener(
      DrawerOpenEvent.eventName,
      this.#handleDrawerOpen
    );

    /*
     * Lissa Costa Rica
     * Autosave del número de cédula como atributo del carrito.
     *
     * Usamos delegación de eventos porque el contenido del carrito
     * puede ser reemplazado dinámicamente por Shopify.
     */
    this.addEventListener('input', this.#handleCedulaInput);
    this.addEventListener('change', this.#handleCedulaChange);
    this.addEventListener('focusout', this.#handleCedulaFocusOut);

    // The restore path sets [open] before this module loads, so the
    // theme-drawer:open event will have already fired. Use the attribute
    // check so this works even before <theme-drawer> upgrades.
    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }

    this.#syncInitialCedulaValue();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(
      StandardEvents.cartLinesUpdate,
      this.#handleCartLinesUpdate
    );

    this.#themeDrawer?.removeEventListener(
      DrawerOpenEvent.eventName,
      this.#handleDrawerOpen
    );

    this.removeEventListener('input', this.#handleCedulaInput);
    this.removeEventListener('change', this.#handleCedulaChange);
    this.removeEventListener('focusout', this.#handleCedulaFocusOut);

    if (this.#cedulaSaveTimer) {
      window.clearTimeout(this.#cedulaSaveTimer);
    }
  }

  /**
   * Guarda como referencia inicial el valor de la cédula
   * que Liquid haya cargado desde cart.attributes.
   */
  #syncInitialCedulaValue() {
    const input = this.querySelector('#lissa-cedula-drawer');

    if (input instanceof HTMLInputElement) {
      this.#lastCedulaSaved = input.value.trim();
    }
  }

  /**
   * Detecta escritura en el campo de cédula.
   * Espera brevemente antes de guardar para evitar una petición
   * a Shopify por cada tecla.
   *
   * @param {Event} event
   */
  #handleCedulaInput = (event) => {
    const target = event.target;

    if (
      !(target instanceof HTMLInputElement) ||
      target.id !== 'lissa-cedula-drawer'
    ) {
      return;
    }

    if (this.#cedulaSaveTimer) {
      window.clearTimeout(this.#cedulaSaveTimer);
    }

    this.#cedulaSaveTimer = window.setTimeout(() => {
      this.#saveCedula(target.value);
    }, 350);
  };

  /**
   * Guarda inmediatamente cuando el valor cambia.
   *
   * @param {Event} event
   */
  #handleCedulaChange = (event) => {
    const target = event.target;

    if (
      !(target instanceof HTMLInputElement) ||
      target.id !== 'lissa-cedula-drawer'
    ) {
      return;
    }

    if (this.#cedulaSaveTimer) {
      window.clearTimeout(this.#cedulaSaveTimer);
    }

    this.#saveCedula(target.value);
  };

  /**
   * Guarda inmediatamente cuando el cliente sale del campo.
   *
   * @param {FocusEvent} event
   */
  #handleCedulaFocusOut = (event) => {
    const target = event.target;

    if (
      !(target instanceof HTMLInputElement) ||
      target.id !== 'lissa-cedula-drawer'
    ) {
      return;
    }

    if (this.#cedulaSaveTimer) {
      window.clearTimeout(this.#cedulaSaveTimer);
    }

    this.#saveCedula(target.value);
  };

  /**
   * Guarda el número de cédula como cart attribute en Shopify.
   *
   * No crea pedidos.
   * No modifica productos.
   * No cambia cantidades.
   *
   * @param {string} value
   */
  async #saveCedula(value) {
    const cedula = value.trim();

    // Evita peticiones innecesarias si Shopify ya tiene este valor.
    if (cedula === this.#lastCedulaSaved) {
      return;
    }

    try {
      const root =
        window.Shopify?.routes?.root ||
        '/';

      const response = await fetch(`${root}cart/update.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          attributes: {
            'Número de cédula': cedula,
          },
        }),

        /*
         * Permite que el navegador intente completar la petición
         * aunque el cliente navegue rápidamente al checkout.
         */
        keepalive: true,
      });

      if (!response.ok) {
        throw new Error(
          `No se pudo guardar la cédula. HTTP ${response.status}`
        );
      }

      const cart = await response.json();

      this.#lastCedulaSaved =
        cart?.attributes?.['Número de cédula'] ?? cedula;

      const input = this.querySelector('#lissa-cedula-drawer');

      if (input instanceof HTMLInputElement) {
        input.dataset.saved = 'true';
      }
    } catch (error) {
      console.warn(
        '[Lissa] No se pudo guardar el número de cédula en el carrito:',
        error
      );

      const input = this.querySelector('#lissa-cedula-drawer');

      if (input instanceof HTMLInputElement) {
        input.dataset.saved = 'false';
      }
    }
  }

  /**
   * Handles the theme-drawer opening — updates sticky state and wires up the installments CTA.
   */
  #handleDrawerOpen = () => {
    this.#updateStickyState();

    /*
     * Cada vez que el drawer abre, sincronizamos nuevamente
     * el valor que Liquid haya renderizado.
     */
    this.#syncInitialCedulaValue();

    // Close cart drawer when installments CTA is clicked to avoid overlapping dialogs.
    // Re-queried on every open so it survives cart content re-renders that
    // replace the shopify-payment-terms shadow root.
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this.querySelector(
        'shopify-payment-terms'
      )?.shadowRoot?.querySelector('#shopify-installments-cta');

      cta?.addEventListener(
        'click',
        () => this.#themeDrawer?.close(),
        { once: true }
      );
    });
  };

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    const shouldAutoOpen =
      this.hasAttribute('auto-open') &&
      event.action === 'add' &&
      !this.#themeDrawer?.isOpen;

    // When the event originates inside an open MODAL <dialog> (e.g. quick-add),
    // defer the auto-open until that dialog's native `close` fires so its focus
    // restoration runs first — otherwise we'd capture the wrong
    // `#previouslyFocused`. Non-modal dialogs (e.g. the hotspot preview) don't
    // close on add and don't move focus, so `:modal` excludes them.
    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element
        ? event.target.closest('dialog:modal')
        : null
    );

    if (
      shouldAutoOpen &&
      !sourceModal &&
      !this.#isCartEmpty()
    ) {
      this.#themeDrawer?.open();
    }

    event.promise
      ?.then(({ detail }) => {
        const settle = () =>
          requestAnimationFrame(() => {
            this.#updateStickyState();
            this.#syncInitialCedulaValue();
          });

        if (!shouldAutoOpen || detail?.didError) {
          settle();
          return;
        }

        const openAndSettle = () => {
          if (!this.#themeDrawer?.isOpen) {
            this.#themeDrawer?.open();
          }

          settle();
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener(
            'close',
            openAndSettle,
            { once: true }
          );
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn(
            '[cart-drawer] Event promise rejected:',
            error
          );
        }
      });
  };

  #isCartEmpty() {
    return Boolean(
      this.querySelector('.cart-drawer--empty')
    );
  }

  #updateStickyState() {
    const dialog = this.#dialog;

    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries
    // (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector(
      '.cart-drawer__content'
    );

    const summary = dialog.querySelector(
      '.cart-drawer__summary'
    );

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode
      // when summary disappears (e.g., empty cart).
      dialog.setAttribute(
        'cart-summary-sticky',
        'false'
      );

      return;
    }

    const drawerHeight =
      dialog.getBoundingClientRect().height;

    const summaryHeight =
      summary.getBoundingClientRect().height;

    const ratio =
      summaryHeight / drawerHeight;

    dialog.setAttribute(
      'cart-summary-sticky',
      ratio > this.#summaryThreshold
        ? 'false'
        : 'true'
    );
  }
}

if (
  !customElements.get('cart-drawer-component')
) {
  customElements.define(
    'cart-drawer-component',
    CartDrawerComponent
  );
}